import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../context/LanguageContext';
import { apiService } from '../services/api';
import { TimeslotInfo, BookAppointmentRequest, UserResponse, TimeslotOverrideResponse, TimeslotClientsResponse, GlobalTimeslotDefinitionResponse, DayOfWeek } from '../types/api';
import { useConfirmation } from '../hooks/useConfirmation';
import { toGreekUpperCase } from '../utils/greekUtils';
import ConfirmationDialog from './ConfirmationDialog';
import AdminCancelAppointmentDialog from './AdminCancelAppointmentDialog';
import './TimeslotManagement.css';
import './Modal.css';

const TimeslotManagement: React.FC = () => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [timeslots, setTimeslots] = useState<TimeslotInfo[]>([]);
  const [clients, setClients] = useState<UserResponse[]>([]);
  const [overrides, setOverrides] = useState<TimeslotOverrideResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const { confirmation, showConfirmation } = useConfirmation();
  const [cancelDialogState, setCancelDialogState] = useState<{ appointmentId: number; fromModal: boolean } | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Booking modal state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedTimeslot, setSelectedTimeslot] = useState<TimeslotInfo | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedClientName, setSelectedClientName] = useState<string>('');
  


  // Change time modal state
  const [showChangeTimeModal, setShowChangeTimeModal] = useState(false);
  const [changeTimeTimeslot, setChangeTimeTimeslot] = useState<TimeslotInfo | null>(null);
  const [newStartTime, setNewStartTime] = useState<string>('');
  const [newEndTime, setNewEndTime] = useState<string>('');
  const [timeChangeReason, setTimeChangeReason] = useState<string>('');

  // Close day modal state
  const [showCloseDayModal, setShowCloseDayModal] = useState(false);
  const [closeDayReason, setCloseDayReason] = useState<string>('');

  // Close date range modal state
  const [showCloseDateRangeModal, setShowCloseDateRangeModal] = useState(false);
  const [closeRangeStartDate, setCloseRangeStartDate] = useState<string>('');
  const [closeRangeEndDate, setCloseRangeEndDate] = useState<string>('');
  const [closeRangeReason, setCloseRangeReason] = useState<string>('');

  // Bulk close timeslots modal state
  const [showBulkCloseTimeslotsModal, setShowBulkCloseTimeslotsModal] = useState(false);
  const [bulkCloseStartDate, setBulkCloseStartDate] = useState<string>('');
  const [bulkCloseEndDate, setBulkCloseEndDate] = useState<string>('');
  const [selectedTimeslots, setSelectedTimeslots] = useState<string[]>([]);
  const [bulkCloseReason, setBulkCloseReason] = useState<string>('');
  const [showBulkConfirmation, setShowBulkConfirmation] = useState(false);
  const [bulkOperationResults, setBulkOperationResults] = useState<{
    successful: number;
    failed: number;
    errors: string[];
  } | null>(null);
  const [globalTimeslots, setGlobalTimeslots] = useState<GlobalTimeslotDefinitionResponse[]>([]);
  const [loadingGlobalTimeslots, setLoadingGlobalTimeslots] = useState(false);

  // Change capacity modal state
  const [showChangeCapacityModal, setShowChangeCapacityModal] = useState(false);
  const [changeCapacityTimeslot, setChangeCapacityTimeslot] = useState<TimeslotInfo | null>(null);
  const [newCapacity, setNewCapacity] = useState<number>(0);
  const [capacityChangeReason, setCapacityChangeReason] = useState<string>('');

  // View clients modal state
  const [showViewClientsModal, setShowViewClientsModal] = useState(false);
  const [timeslotClients, setTimeslotClients] = useState<TimeslotClientsResponse | null>(null);
  const [loadingClients, setLoadingClients] = useState(false);
  
  // Use refs to track API calls and prevent duplicates
  const clientsFetchedRef = useRef(false);
  const currentFetchRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchKey = `${selectedDate}`;
    
    // Prevent duplicate calls for the same date
    if (currentFetchRef.current === fetchKey || isLoading) {
      return;
    }
    
    currentFetchRef.current = fetchKey;
    
    // Use combined function for initial load to prevent duplicate calls
    fetchTimeslotsAndOverrides();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);



  const fetchTimeslotsAndOverrides = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Fetch both timeslots and overrides in parallel
      const [timeslotsResponse, overridesResponse] = await Promise.all([
        apiService.getTimeslots(selectedDate),
        apiService.getTimeslotOverrides(selectedDate)
      ]);
      
      // Handle timeslots response
      if (timeslotsResponse.success && timeslotsResponse.data) {
        setTimeslots(timeslotsResponse.data.timeslots);
      }
      
      // Handle overrides response
      if (overridesResponse.success && overridesResponse.data) {
        setOverrides(overridesResponse.data);
      }
      
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(t('failedToLoadData'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTimeslots = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await apiService.getTimeslots(selectedDate);
      if (response.success && response.data) {
        let timeslotData = response.data.timeslots;

        // If the selected date is today, filter out past timeslots
        const today = new Date().toISOString().split('T')[0];
        if (selectedDate === today) {
          const now = new Date();
          const currentTime = now.getHours() * 60 + now.getMinutes(); // Convert to minutes
          
          timeslotData = timeslotData.filter(slot => {
            const [hours, minutes] = slot.startTime.split(':').map(Number);
            const slotTime = hours * 60 + minutes;
            return slotTime > currentTime; // Only show future timeslots
          });
        }

        setTimeslots(timeslotData);
      }
    } catch (err: any) {
      console.error('Error fetching timeslots:', err);
      setError(t('failedToLoadTimeslots'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await apiService.getClients();
      if (response.success && response.data) {
        // Handle both paginated and non-paginated responses
        // The API now returns a simple array of UserResponse
        const clientsData = response.data as UserResponse[];
        setClients(clientsData.filter((client: UserResponse) => client.isActive));
      }
      clientsFetchedRef.current = true;
    } catch (err: any) {
      console.error('Error fetching clients:', err);
      clientsFetchedRef.current = true; // Set flag even on error to prevent infinite retries
    }
  };

  const fetchOverrides = async () => {
    try {
      const response = await apiService.getTimeslotOverrides(selectedDate);
      if (response.success && response.data) {
        setOverrides(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching overrides:', err);
    }
  };

  const getTimeslotWithOverrides = (timeslot: TimeslotInfo) => {
    // No need to manually check for overrides anymore - the API provides this information directly
    return {
      ...timeslot,
      hasTimeOverride: timeslot.hasTimeChange || false
    };
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const currentDate = new Date(selectedDate);
    if (direction === 'prev') {
      currentDate.setDate(currentDate.getDate() - 1);
    } else {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const handlePrevDay = () => {
    navigateDate('prev');
  };

  const handleNextDay = () => {
    navigateDate('next');
  };

  const openBookingModal = (timeslot: TimeslotInfo) => {
    setSelectedTimeslot(timeslot);
    setSelectedClientId('');
    setShowBookingModal(true);
    
    // Fetch clients only when booking modal is opened
    if (!clientsFetchedRef.current) {
      fetchClients();
    }
  };



  const openChangeTimeModal = (timeslot: TimeslotInfo) => {
    setChangeTimeTimeslot(timeslot);
    setNewStartTime(timeslot.startTime);
    setNewEndTime(timeslot.endTime);
    setTimeChangeReason('');
    setShowChangeTimeModal(true);
  };

  const openChangeCapacityModal = (timeslot: TimeslotInfo) => {
    setChangeCapacityTimeslot(timeslot);
    setNewCapacity(timeslot.capacity);
    setCapacityChangeReason('');
    setShowChangeCapacityModal(true);
  };

  const handleViewClients = async (timeslot: TimeslotInfo) => {
    setLoadingClients(true);
    setShowViewClientsModal(true);
    setTimeslotClients(null);

    try {
      // Use original times if available, otherwise fall back to current times
      const startTime = timeslot.originalStartTime || timeslot.startTime;
      const endTime = timeslot.originalEndTime || timeslot.endTime;
      
      const response = await apiService.getTimeslotClients(
        selectedDate,
        startTime,
        endTime
      );
      
      if (response.success && response.data) {
        setTimeslotClients(response.data);
      } else {
        setError(response.message || t('failedToLoadClients'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('error'));
    } finally {
      setLoadingClients(false);
    }
  };

  const handleClientSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSelectedClientName(value);
    
    // Find client by name and email combination
    const client = clients.find(c => `${c.name} (${c.email})` === value);
    if (client) {
      setSelectedClientId(client.id.toString());
    } else {
      setSelectedClientId('');
    }
  };

  const handleBookClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTimeslot || !selectedClientId) return;

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const bookingData: BookAppointmentRequest = {
        appointmentDate: selectedDate,
        startTime: selectedTimeslot.startTime,
        endTime: selectedTimeslot.endTime,
        userId: parseInt(selectedClientId),
        isAdminOverride: true
      };

      const response = await apiService.bookAppointmentAsAdmin(bookingData);
      if (response.success) {
        setSuccess(t('success'));
        setShowBookingModal(false);
        fetchTimeslots(); // Refresh timeslots
        
        // Trigger global event to refresh dashboard
        window.dispatchEvent(new CustomEvent('appointmentBooked'));
      } else {
        setError(response.message || t('error'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('error'));
    } finally {
      setIsLoading(false);
    }
  };



  const handleChangeTime = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!changeTimeTimeslot) return;

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.updateTimeslotTimeSimple(
        selectedDate,
        changeTimeTimeslot.startTime,
        changeTimeTimeslot.endTime,
        newStartTime,
        newEndTime,
        timeChangeReason
      );

      if (response.success) {
        setSuccess(t('success'));
        setShowChangeTimeModal(false);
        fetchTimeslots();
        fetchOverrides();
      } else {
        setError(response.message || t('error'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeCapacity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!changeCapacityTimeslot) return;

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.updateTimeslotCapacitySimple(
        selectedDate,
        changeCapacityTimeslot.startTime,
        changeCapacityTimeslot.endTime,
        newCapacity,
        capacityChangeReason
      );

      if (response.success) {
        setSuccess(t('success'));
        setShowChangeCapacityModal(false);
        fetchTimeslots();
        fetchOverrides();
      } else {
        setError(response.message || t('error'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableTimeslot = async (timeslot: TimeslotInfo) => {
    const reason = prompt(t('reasonForDisabling', 'Reason for disabling this timeslot (optional):'));
    if (reason === null) return; // User cancelled

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.disableTimeslotSimple(
        selectedDate,
        timeslot.startTime,
        timeslot.endTime,
        reason
      );

      if (response.success) {
        setSuccess(t('success'));
        fetchTimeslots();
        fetchOverrides();
      } else {
        setError(response.message || t('error'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDay = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.disableDay({
        date: selectedDate,
        reason: closeDayReason
      });

      if (response.success) {
        setSuccess(t('success'));
        setShowCloseDayModal(false);
        setCloseDayReason('');
        fetchTimeslots();
        fetchOverrides();
      } else {
        setError(response.message || t('error'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDateRange = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.disableDateRange({
        startDate: closeRangeStartDate,
        endDate: closeRangeEndDate,
        reason: closeRangeReason
      });

      if (response.success) {
        setSuccess(t('success'));
        setShowCloseDateRangeModal(false);
        setCloseRangeStartDate('');
        setCloseRangeEndDate('');
        setCloseRangeReason('');
        fetchTimeslots();
        fetchOverrides();
      } else {
        setError(response.message || t('error'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkCloseTimeslots = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTimeslots.length === 0) {
      setError(t('pleaseSelectAtLeastOneTimeslot'));
      return;
    }
    setShowBulkConfirmation(true);
  };

  const executeBulkCloseTimeslots = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    setBulkOperationResults(null);

    const startDate = new Date(bulkCloseStartDate);
    const endDate = new Date(bulkCloseEndDate);
    const dates: string[] = [];
    
    // Generate all dates in the range
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split('T')[0]);
    }

    let successful = 0;
    let failed = 0;
    const errors: string[] = [];

    try {
      // Execute all combinations of dates and timeslots
      for (const date of dates) {
        for (const timeslot of selectedTimeslots) {
          const [startTime, endTime] = timeslot.split('-');
          // Ensure time format is HH:MM:SS for API
          const formattedStartTime = startTime.length === 5 ? `${startTime}:00` : startTime;
          const formattedEndTime = endTime.length === 5 ? `${endTime}:00` : endTime;
          try {
            const response = await apiService.disableTimeslotSimple(
              date,
              formattedStartTime,
              formattedEndTime,
              bulkCloseReason
            );
            if (response.success) {
              successful++;
            } else {
              failed++;
              errors.push(`${date} ${timeslot}: ${response.message || t('error')}`);
            }
          } catch (err: any) {
            failed++;
            errors.push(`${date} ${timeslot}: ${err.response?.data?.message || t('error')}`);
          }
        }
      }

      setBulkOperationResults({ successful, failed, errors });
      
      if (failed === 0) {
        setSuccess(t('bulkOperationCompletedSuccessfully', `Successfully closed ${successful} timeslots`));
      } else if (successful > 0) {
        setSuccess(t('bulkOperationPartialSuccess', `${successful} timeslots closed successfully, ${failed} failed`));
      } else {
        setError(t('bulkOperationFailed', 'All operations failed'));
      }

      // Reset form
      setBulkCloseStartDate('');
      setBulkCloseEndDate('');
      setSelectedTimeslots([]);
      setBulkCloseReason('');
      setShowBulkCloseTimeslotsModal(false);
      
      fetchTimeslots();
      fetchOverrides();
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimeslotSelection = (timeslot: string) => {
    setSelectedTimeslots(prev => 
      prev.includes(timeslot) 
        ? prev.filter(t => t !== timeslot)
        : [...prev, timeslot]
    );
  };

  const fetchGlobalTimeslots = async () => {
    setLoadingGlobalTimeslots(true);
    try {
      // Get all unique timeslots from all days of the week
      const allTimeslots: GlobalTimeslotDefinitionResponse[] = [];
      const timeslotSet = new Set<string>();
      
      // Fetch timeslots for each day of the week
      for (let day = 1; day <= 7; day++) {
        const dayOfWeek = day === 7 ? 0 : day; // Convert to DayOfWeek enum (Sunday = 0, Monday = 1, etc.)
        const response = await apiService.getGlobalTimeslots(dayOfWeek as DayOfWeek);
        if (response.success && response.data) {
          response.data.forEach(timeslot => {
            const timeslotKey = `${timeslot.effectiveStartTime}-${timeslot.effectiveEndTime}`;
            if (!timeslotSet.has(timeslotKey) && timeslot.isEnabled) {
              timeslotSet.add(timeslotKey);
              allTimeslots.push(timeslot);
            }
          });
        }
      }
      
      // Sort timeslots by start time
      allTimeslots.sort((a, b) => a.effectiveStartTime.localeCompare(b.effectiveStartTime));
      setGlobalTimeslots(allTimeslots);
    } catch (err: any) {
      console.error('Error fetching global timeslots:', err);
    } finally {
      setLoadingGlobalTimeslots(false);
    }
  };

  const openBulkCloseModal = () => {
    // Clear previous results and reset confirmation state
    setBulkOperationResults(null);
    setShowBulkConfirmation(false);
    setShowBulkCloseTimeslotsModal(true);
    // Fetch global timeslots when opening the modal
    fetchGlobalTimeslots();
  };

  const handleRemoveOverride = async (overrideId: number) => {
    showConfirmation({
      title: t('removeOverride'),
      message: t('areYouSure') + ' ' + t('thisActionCannotBeUndone'),
      confirmText: t('removeOverride'),
      type: 'danger',
      onConfirm: () => {
        confirmRemoveOverride(overrideId);
      }
    });
  };

  const confirmRemoveOverride = async (overrideId: number) => {
    try {
      const response = await apiService.deleteTimeslotOverride(overrideId);
      if (response.success) {
        setSuccess(t('success'));
        fetchTimeslots();
        fetchOverrides();
      } else {
        setError(response.message || t('error'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('error'));
    }
  };

  const handleCancelAppointment = (appointmentId: number) => {
    setCancelDialogState({ appointmentId, fromModal: false });
  };

  const confirmCancelAppointment = async (appointmentId: number, countTowardLimit: boolean) => {
    try {
      const response = await apiService.cancelAppointmentAsAdmin(appointmentId, countTowardLimit);
      if (response.success) {
        setSuccess(t('appointmentCancelledSuccessfully'));
        fetchTimeslots(); // Refresh timeslots

        // Clear success message after 4 seconds
        setTimeout(() => {
          setSuccess('');
        }, 4000);

        // Trigger global event to refresh dashboard
        window.dispatchEvent(new CustomEvent('appointmentCancelled'));
      } else {
        setError(response.message || t('error'));

        // Clear error message after 6 seconds
        setTimeout(() => {
          setError('');
        }, 6000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('error'));

      // Clear error message after 6 seconds
      setTimeout(() => {
        setError('');
      }, 6000);
    }
  };

  const handleCancelAppointmentFromModal = (appointmentId: number) => {
    setCancelDialogState({ appointmentId, fromModal: true });
  };

  const confirmCancelAppointmentFromModal = async (appointmentId: number, countTowardLimit: boolean) => {
    try {
      const response = await apiService.cancelAppointmentAsAdmin(appointmentId, countTowardLimit);
      if (response.success) {
        setSuccess(t('appointmentCancelledSuccessfully'));

        // Refresh both the main timeslots and the modal data
        fetchTimeslots();
        
        // Refresh the modal data if it's open
        if (timeslotClients) {
          const refreshResponse = await apiService.getTimeslotClients(
            selectedDate,
            timeslotClients.originalStartTime,
            timeslotClients.originalEndTime
          );
          
          if (refreshResponse.success && refreshResponse.data) {
            setTimeslotClients(refreshResponse.data);
          }
        }
        
        // Clear success message after 4 seconds
        setTimeout(() => {
          setSuccess('');
        }, 4000);
        
        // Trigger global event to refresh dashboard
        window.dispatchEvent(new CustomEvent('appointmentCancelled'));
      } else {
        setError(response.message || t('error'));

        // Clear error message after 6 seconds
        setTimeout(() => {
          setError('');
        }, 6000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('error'));

      // Clear error message after 6 seconds
      setTimeout(() => {
        setError('');
      }, 6000);
    }
  };

  const handleConfirmCancelAppointment = async (countTowardLimit: boolean) => {
    if (!cancelDialogState) return;
    const { appointmentId, fromModal } = cancelDialogState;

    setIsCancelling(true);
    try {
      if (fromModal) {
        await confirmCancelAppointmentFromModal(appointmentId, countTowardLimit);
      } else {
        await confirmCancelAppointment(appointmentId, countTowardLimit);
      }
    } finally {
      setIsCancelling(false);
      setCancelDialogState(null);
    }
  };

  const formatTime = (timeString: string) => {
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      const hours = parseInt(parts[0]);
      const minutes = parts[1];
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }
    return timeString;
  };

  const formatTimeSpan = (timeSpan: any) => {
    // Handle simple time string format (e.g., "10:00:00")
    if (typeof timeSpan === 'string') {
      return formatTime(timeSpan.substring(0, 5)); // Extract HH:MM and format
    }
    
    // Handle TimeSpan object format
    if (timeSpan && timeSpan.hours !== undefined && timeSpan.minutes !== undefined) {
      const hours = timeSpan.hours;
      const minutes = timeSpan.minutes.toString().padStart(2, '0');
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }
    return t('notAvailable', 'N/A');
  };

  const formatDateDisplay = (dateString: string) => {
    // Convert YYYY-MM-DD to DD-MM-YYYY
    const parts = dateString.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateString;
  };

  const getOverrideDescription = (override: TimeslotOverrideResponse) => {
    if (override.isFullDayOverride) {
      return t('closedEntireDay', 'Closed the entire day') + `${override.reason ? ` - ${override.reason}` : ''}`;
    }
    
    if (override.isDisabled) {
      return t('disabledTimeslotFrom', 'Disabled timeslot from') + ` ${formatTimeSpan(override.startTime)} ${t('to')} ${formatTimeSpan(override.endTime)}${override.reason ? ` - ${override.reason}` : ''}`;
    }
    
    if (override.capacityOverride) {
      return t('changedCapacityTo', 'Changed capacity to') + ` ${override.capacityOverride} ${t('for')} ${formatTimeSpan(override.startTime)} - ${formatTimeSpan(override.endTime)}${override.reason ? ` - ${override.reason}` : ''}`;
    }
    
    if (override.newStartTime && override.newEndTime) {
      const originalTime = `${formatTimeSpan(override.startTime)} - ${formatTimeSpan(override.endTime)}`;
      const newTime = `${formatTimeSpan(override.newStartTime)} - ${formatTimeSpan(override.newEndTime)}`;
      return t('changedTimeFrom', 'Changed time from') + ` ${originalTime} ${t('to')} ${newTime}${override.reason ? ` - ${override.reason}` : ''}`;
    }
    
    return t('modifiedTimeslot', 'Modified timeslot') + `${override.reason ? ` - ${override.reason}` : ''}`;
  };

  const getTimeslotOverrideDescription = (timeslot: TimeslotInfo) => {
    const descriptions = [];
    
    if (timeslot.hasTimeChange && timeslot.originalStartTime && timeslot.originalEndTime) {
      const originalTime = `${formatTime(timeslot.originalStartTime)} - ${formatTime(timeslot.originalEndTime)}`;
      const newTime = `${formatTime(timeslot.startTime)} - ${formatTime(timeslot.endTime)}`;
      descriptions.push(t('timeChangedFrom', 'Time changed from') + ` ${originalTime} ${t('to')} ${newTime}`);
    }
    
    if (timeslot.hasCapacityOverride) {
      descriptions.push(t('capacityModified', 'Capacity modified'));
    }
    
    if (timeslot.overrideReason) {
      descriptions.push(t('reason') + `: ${timeslot.overrideReason}`);
    }
    
    return descriptions.join(' | ');
  };

  const getAvailabilityStatus = (timeslot: TimeslotInfo) => {
    // Check if timeslot is disabled first
    if (timeslot.isDisabled) return 'disabled';
    // Admin can override capacity, so don't show as unavailable based on capacity
    if (timeslot.currentBookings >= timeslot.capacity) return 'full';
    if (timeslot.currentBookings > 0) return 'partial';
    return 'available';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return '#22c55e';
      case 'partial':
        return '#f59e0b';
      case 'full':
        return '#ef4444';
      case 'disabled':
        return '#6b7280';
      case 'unavailable':
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  return (
    <div className="timeslot-management">
      <div className="timeslot-header">
        <h2>{t('timeslotManagement')}</h2>
        <div className="header-controls">
          <div className="date-selector">
            <label htmlFor="date">{t('selectDate')}:</label>
            <div className="date-navigation">
              <button
                className="date-nav-btn"
                onClick={handlePrevDay}
                disabled={isLoading}
                title={t('previousDay')}
              >
                ←
              </button>
              <input
                type="date"
                id="date"
                value={selectedDate}
                onChange={handleDateChange}
              />
              <button
                className="date-nav-btn"
                onClick={handleNextDay}
                disabled={isLoading}
                title={t('nextDay')}
              >
                →
              </button>
            </div>
          </div>
          <button
            className="btn-close-day"
            onClick={() => setShowCloseDayModal(true)}
            disabled={isLoading}
          >
            {t('closeDay')}
          </button>
          <button
            className="btn-close-range"
            onClick={() => setShowCloseDateRangeModal(true)}
            disabled={isLoading}
          >
            {t('closeDateRange')}
          </button>
          <button
            className="btn-bulk-close-timeslots"
            onClick={openBulkCloseModal}
            disabled={isLoading}
          >
            {t('bulkCloseTimeslots')}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button 
            type="button" 
            className="message-close-btn" 
            onClick={() => setError('')}
            title={t('close')}
          >
            ×
          </button>
        </div>
      )}
      {success && (
        <div className="success-message">
          {success}
          <button 
            type="button" 
            className="message-close-btn" 
            onClick={() => setSuccess('')}
            title={t('close')}
          >
            ×
          </button>
        </div>
      )}

      {/* Overrides Section */}
      {overrides.length > 0 && (
        <div className="overrides-section">
          <h3>{t('overrides')} {t('for')} {selectedDate}</h3>
          <div className="overrides-list">
            {overrides.map((override) => (
              <div key={override.id} className="override-item">
                <div className="override-info">
                  <div className="override-description">
                    {getOverrideDescription(override)}
                  </div>
                  <div className="override-meta">
                    <span className="override-creator">{t('modifiedBy', 'Modified by')}: {override.createdByName}</span>
                    <span className="override-date">
                      {t('on', 'on')} {new Date(override.createdAt).toLocaleDateString()} {t('at', 'at')} {new Date(override.createdAt).toLocaleTimeString('en-GB', { hour12: false })}
                    </span>
                  </div>
                </div>
                <button
                  className="btn-remove-override"
                  onClick={() => handleRemoveOverride(override.id)}
                  title={t('removeOverride')}
                >
                  {t('removeOverride')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="loading">{t('loading')}...</div>
      ) : (
        <div className="timeslots-grid">
          {timeslots.length === 0 ? (
            <div className="no-timeslots">
              <p>{t('noTimeslotsForDate')}</p>
            </div>
          ) : (
            timeslots.map((timeslot) => {
              const timeslotWithOverrides = getTimeslotWithOverrides(timeslot);
              const status = getAvailabilityStatus(timeslotWithOverrides);
              return (
                <div 
                  key={timeslot.id} 
                  className={`timeslot-card ${status} ${timeslotWithOverrides.hasTimeOverride ? 'has-time-override' : ''} ${timeslot.hasOverride ? 'has-override' : ''}`}
                  style={{ borderLeftColor: getStatusColor(status) }}
                >
                  <div className="timeslot-header-info">
                    <div className="time-range">
                      {formatTime(timeslotWithOverrides.startTime)} - {formatTime(timeslotWithOverrides.endTime)}
                      {timeslotWithOverrides.hasTimeOverride && (
                        <span className="override-indicator" title={t('timeHasBeenOverridden', 'Time has been overridden')}>⚡</span>
                      )}
                      {timeslot.hasCapacityOverride && (
                        <span className="capacity-override-indicator" title={t('capacityHasBeenOverridden', 'Capacity has been overridden')}>📊</span>
                      )}
                    </div>
                    <div className="capacity-info">
                      {timeslotWithOverrides.currentBookings}/{timeslotWithOverrides.capacity}
                    </div>
                  </div>

                  <div className="timeslot-status">
                    <span className={`status-indicator ${status}`}>
                      {status === 'available' && t('available')}
                      {status === 'partial' && t('booked')}
                      {status === 'full' && t('full')}
                      {status === 'disabled' && t('disabled')}
                    </span>
                  </div>

                  {/* Show override information */}
                  {timeslot.hasOverride && (
                    <div className="timeslot-override-info">
                      <span className="override-description">
                        {getTimeslotOverrideDescription(timeslot)}
                      </span>
                    </div>
                  )}

                  <div className="timeslot-actions">
                    <button
                      className="btn-view-clients"
                      onClick={() => handleViewClients(timeslot)}
                    >
                      {t('viewClients')}
                    </button>
                    <button
                      className="btn-change-time"
                      onClick={() => openChangeTimeModal(timeslot)}
                    >
                      {t('changeTime')}
                    </button>
                    <button
                      className="btn-change-capacity"
                      onClick={() => openChangeCapacityModal(timeslotWithOverrides)}
                    >
                      {t('changeCapacity')}
                    </button>
                    <button
                      className="btn-disable"
                      onClick={() => handleDisableTimeslot(timeslot)}
                    >
                      {t('disableTimeslot')}
                    </button>
                    <button
                      className="btn-book"
                      onClick={() => openBookingModal(timeslotWithOverrides)}
                    >
                      {t('bookClient')}
                    </button>
                  </div>

                  {/* Show current appointments */}
                  {timeslotWithOverrides.appointments && timeslotWithOverrides.appointments.length > 0 && (
                    <div className="current-appointments">
                      <h4>{t('appointments')}:</h4>
                      {timeslotWithOverrides.appointments.map((appointment) => (
                        <div key={appointment.id} className="appointment-item">
                          <span className="client-name">{appointment.userName}</span>
                          {appointment.status === 1 && (
                            <button
                              className="btn-cancel-small"
                              onClick={() => handleCancelAppointment(appointment.id)}
                              title={t('cancel') + ' ' + t('appointments')}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && selectedTimeslot && (
        <div className="modal-overlay" onClick={() => setShowBookingModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('bookClient')} {t('for')} {formatTime(selectedTimeslot.startTime)} - {formatTime(selectedTimeslot.endTime)}</h3>
              <button className="modal-close" onClick={() => setShowBookingModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleBookClient} className="modal-form">
              <div className="form-group">
                <label htmlFor="clientSelect">{t('selectClient')}:</label>
                <input
                  list="clientsList"
                  id="clientSelect"
                  value={selectedClientName}
                  onChange={handleClientSearch}
                  placeholder={t('selectClientPlaceholder')}
                  required
                  disabled={isLoading}
                />
                <datalist id="clientsList">
                  {clients.map((client) => (
                    <option key={client.id} value={`${client.name} (${client.email})`} />
                  ))}
                </datalist>
              </div>
              
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowBookingModal(false)}
                  className="btn-secondary"
                  disabled={isLoading}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!selectedClientId || isLoading}
                >
                  {isLoading ? t('booking') : t('bookClient')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}



      {/* Change Time Modal */}
      {showChangeTimeModal && changeTimeTimeslot && (
        <div className="modal-overlay" onClick={() => setShowChangeTimeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('changeTime')} {t('for')} {formatTime(changeTimeTimeslot.startTime)} - {formatTime(changeTimeTimeslot.endTime)}</h3>
              {changeTimeTimeslot.hasTimeChange && changeTimeTimeslot.originalStartTime && changeTimeTimeslot.originalEndTime && (
                <p className="original-time-info">
                  {t('originalTime')}: {formatTime(changeTimeTimeslot.originalStartTime)} - {formatTime(changeTimeTimeslot.originalEndTime)}
                </p>
              )}
              <button className="modal-close" onClick={() => setShowChangeTimeModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleChangeTime} className="modal-form">
              <div className="form-group">
                <label htmlFor="newStartTime">{t('newStartTime')}:</label>
                <input
                  type="time"
                  id="newStartTime"
                  value={newStartTime}
                  onChange={(e) => setNewStartTime(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="newEndTime">{t('newEndTime')}:</label>
                <input
                  type="time"
                  id="newEndTime"
                  value={newEndTime}
                  onChange={(e) => setNewEndTime(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="timeChangeReason">{t('reason')} ({t('optional')}):</label>
                <textarea
                  id="timeChangeReason"
                  value={timeChangeReason}
                  onChange={(e) => setTimeChangeReason(e.target.value)}
                  rows={3}
                  disabled={isLoading}
                />
              </div>
              
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowChangeTimeModal(false)}
                  className="btn-secondary"
                  disabled={isLoading}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? t('updating') : t('changeTime')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Capacity Modal */}
      {showChangeCapacityModal && changeCapacityTimeslot && (
        <div className="modal-overlay" onClick={() => setShowChangeCapacityModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('changeCapacityFor')} {formatTime(changeCapacityTimeslot.startTime)} - {formatTime(changeCapacityTimeslot.endTime)}</h3>
              <button className="modal-close" onClick={() => setShowChangeCapacityModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleChangeCapacity} className="modal-form">
              <div className="form-group">
                <label htmlFor="newCapacity">{t('newCapacity')}:</label>
                <input
                  type="number"
                  id="newCapacity"
                  min="1"
                  max="100"
                  value={newCapacity}
                  onChange={(e) => setNewCapacity(parseInt(e.target.value))}
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="capacityChangeReason">{t('reason')} ({t('optional')}):</label>
                <textarea
                  id="capacityChangeReason"
                  value={capacityChangeReason}
                  onChange={(e) => setCapacityChangeReason(e.target.value)}
                  rows={3}
                  disabled={isLoading}
                />
              </div>
              
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowChangeCapacityModal(false)}
                  className="btn-secondary"
                  disabled={isLoading}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? t('updating') : t('changeCapacity')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Clients Modal */}
      {showViewClientsModal && (
        <div className="modal-overlay" onClick={() => setShowViewClientsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('timeslotClients')}</h3>
              <button className="modal-close" onClick={() => setShowViewClientsModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              {loadingClients ? (
                <div className="loading">{t('loading')}...</div>
              ) : timeslotClients ? (
                <div className="clients-info">
                  <div className="timeslot-details">
                    <h4>{t('timeslotDetails', 'Timeslot Details')}</h4>
                    <div className="detail-row">
                      <span className="label">{t('date')}:</span>
                      <span className="value">{new Date(timeslotClients.date).toLocaleDateString()}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">{t('time')}:</span>
                      <span className="value">
                        {formatTime(timeslotClients.effectiveStartTime)} - {formatTime(timeslotClients.effectiveEndTime)}
                        {timeslotClients.hasTimeOverride && (
                          <span className="override-note">
                            ({t('originalTime')}: {formatTime(timeslotClients.originalStartTime)} - {formatTime(timeslotClients.originalEndTime)})
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="label">{t('capacity')}:</span>
                      <span className="value">{timeslotClients.bookedCount}/{timeslotClients.totalCapacity}</span>
                    </div>
                    {timeslotClients.overrideReason && (
                      <div className="detail-row">
                        <span className="label">{t('overrideReason', 'Override Reason')}:</span>
                        <span className="value">{timeslotClients.overrideReason}</span>
                      </div>
                    )}
                  </div>
                  
                  {timeslotClients.bookedClients.length > 0 ? (
                    <div className="booked-clients">
                      <h4>{t('timeslotClients')} ({timeslotClients.bookedCount})</h4>
                      <div className="clients-list">
                                                 {timeslotClients.bookedClients.map((client) => (
                           <div key={client.id} className="client-item">
                             <div className="client-info">
                               <div className="client-name">{client.userName}</div>
                               <div className="booking-details">
                                 <span className="booked-at">
                                   {t('booked')}: {new Date(client.bookedAt).toLocaleDateString()} {t('at')} {new Date(client.bookedAt).toLocaleTimeString('en-GB', { hour12: false })}
                                 </span>
                                 <span className="booked-by">{t('by', 'by')} {client.bookedByName}</span>
                               </div>
                             </div>
                             <div className="client-actions">
                               <div className="client-status">
                                 <span className={`status-badge ${client.status === 1 ? 'active' : 'cancelled'}`}>
                                   {client.status === 1 ? toGreekUpperCase(t('active')) : toGreekUpperCase(t('cancelled'))}
                                 </span>
                               </div>
                               {client.status === 1 && (
                                 <button
                                   className="btn-cancel-appointment"
                                   onClick={() => handleCancelAppointmentFromModal(client.id)}
                                   title={t('cancel') + ' ' + t('appointments')}
                                 >
                                   {t('cancel')}
                                 </button>
                               )}
                             </div>
                           </div>
                         ))}
                      </div>
                    </div>
                  ) : (
                    <div className="no-bookings">
                      <p>{t('noClientsBooked')}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="error-message">{t('failedToLoadProfile')}</div>
              )}
            </div>
            
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowViewClientsModal(false)}
                className="btn-secondary"
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Day Modal */}
      {showCloseDayModal && (
        <div className="modal-overlay" onClick={() => setShowCloseDayModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('closeDay')} - {formatDateDisplay(selectedDate)}</h3>
              <button className="modal-close" onClick={() => setShowCloseDayModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleCloseDay} className="modal-form">
              <div className="form-group">
                <label htmlFor="closeDayReason">{t('reasonForClosing')} ({t('optional')}):</label>
                <textarea
                  id="closeDayReason"
                  value={closeDayReason}
                  onChange={(e) => setCloseDayReason(e.target.value)}
                  rows={3}
                  disabled={isLoading}
                  placeholder={t('closeDayPlaceholder')}
                />
              </div>
              
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowCloseDayModal(false)}
                  className="btn-secondary"
                  disabled={isLoading}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-danger"
                  disabled={isLoading}
                >
                  {isLoading ? t('closing') : t('closeDay')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Close Date Range Modal */}
      {showCloseDateRangeModal && (
        <div className="modal-overlay" onClick={() => setShowCloseDateRangeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('closeDateRange')}</h3>
              <button className="modal-close" onClick={() => setShowCloseDateRangeModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleCloseDateRange} className="modal-form">
              <div className="form-group">
                <label htmlFor="closeRangeStartDate">{t('startDate')}:</label>
                <input
                  type="date"
                  id="closeRangeStartDate"
                  value={closeRangeStartDate}
                  onChange={(e) => setCloseRangeStartDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="closeRangeEndDate">{t('endDate')}:</label>
                <input
                  type="date"
                  id="closeRangeEndDate"
                  value={closeRangeEndDate}
                  onChange={(e) => setCloseRangeEndDate(e.target.value)}
                  min={closeRangeStartDate || new Date().toISOString().split('T')[0]}
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="closeRangeReason">{t('reasonForClosing')} ({t('optional')}):</label>
                <textarea
                  id="closeRangeReason"
                  value={closeRangeReason}
                  onChange={(e) => setCloseRangeReason(e.target.value)}
                  rows={3}
                  disabled={isLoading}
                  placeholder={t('closeDateRangePlaceholder')}
                />
              </div>
              
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowCloseDateRangeModal(false)}
                  className="btn-secondary"
                  disabled={isLoading}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-danger"
                  disabled={isLoading}
                >
                  {isLoading ? t('closing') : t('closeDateRange')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Close Timeslots Modal */}
      {showBulkCloseTimeslotsModal && (
        <div className="modal-overlay" onClick={() => setShowBulkCloseTimeslotsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('bulkCloseTimeslots')}</h3>
              <button className="modal-close" onClick={() => setShowBulkCloseTimeslotsModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleBulkCloseTimeslots} className="modal-form">
              <div className="form-group">
                <label htmlFor="bulkCloseStartDate">{t('startDate')}:</label>
                <input
                  type="date"
                  id="bulkCloseStartDate"
                  value={bulkCloseStartDate}
                  onChange={(e) => setBulkCloseStartDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="bulkCloseEndDate">{t('endDate')}:</label>
                <input
                  type="date"
                  id="bulkCloseEndDate"
                  value={bulkCloseEndDate}
                  onChange={(e) => setBulkCloseEndDate(e.target.value)}
                  min={bulkCloseStartDate || new Date().toISOString().split('T')[0]}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label>{t('selectTimeslotsToClose')}:</label>
                {loadingGlobalTimeslots ? (
                  <div className="loading">{t('loading')}...</div>
                ) : (
                  <div className="timeslot-selection-grid">
                    {globalTimeslots.map((timeslot) => {
                      const timeslotKey = `${timeslot.effectiveStartTime.substring(0, 5)}-${timeslot.effectiveEndTime.substring(0, 5)}`;
                      return (
                        <label key={timeslotKey} className="timeslot-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedTimeslots.includes(timeslotKey)}
                            onChange={() => handleTimeslotSelection(timeslotKey)}
                            disabled={isLoading}
                          />
                          <span className="checkbox-label">{timeslotKey}</span>
                        </label>
                      );
                    })}
                    {globalTimeslots.length === 0 && (
                      <p className="no-timeslots">{t('noGlobalTimeslotsConfigured', 'No global timeslots configured')}</p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="bulkCloseReason">{t('reasonForClosing')} ({t('optional')}):</label>
                <textarea
                  id="bulkCloseReason"
                  value={bulkCloseReason}
                  onChange={(e) => setBulkCloseReason(e.target.value)}
                  rows={3}
                  disabled={isLoading}
                  placeholder={t('bulkCloseReasonPlaceholder')}
                />
              </div>
              
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowBulkCloseTimeslotsModal(false)}
                  className="btn-secondary"
                  disabled={isLoading}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-danger"
                  disabled={isLoading || selectedTimeslots.length === 0}
                >
                  {t('reviewAndClose')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Close Confirmation Modal */}
      {showBulkConfirmation && (
        <div className="modal-overlay" onClick={() => setShowBulkConfirmation(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('confirmBulkClose')}</h3>
              <button className="modal-close" onClick={() => setShowBulkConfirmation(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <p>{t('bulkCloseConfirmationMessage')}</p>
              <div className="confirmation-details">
                <div className="detail-row">
                  <span className="label">{t('dateRange')}:</span>
                  <span className="value">{bulkCloseStartDate} {t('to')} {bulkCloseEndDate}</span>
                </div>
                <div className="detail-row">
                  <span className="label">{t('selectedTimeslots')}:</span>
                  <span className="value">{selectedTimeslots.join(', ')}</span>
                </div>
                {bulkCloseReason && (
                  <div className="detail-row">
                    <span className="label">{t('reason')}:</span>
                    <span className="value">{bulkCloseReason}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="label">{t('totalOperations')}:</span>
                  <span className="value">
                    {(() => {
                      const startDate = new Date(bulkCloseStartDate);
                      const endDate = new Date(bulkCloseEndDate);
                      const days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24)) + 1;
                      return days * selectedTimeslots.length;
                    })()}
                  </span>
                </div>
              </div>

              {bulkOperationResults && (
                <div className="operation-results">
                  <h4>{t('operationResults')}:</h4>
                  <div className="results-summary">
                    <div className="success-count">{t('successful')}: {bulkOperationResults.successful}</div>
                    <div className="failed-count">{t('failed')}: {bulkOperationResults.failed}</div>
                  </div>
                  {bulkOperationResults.errors.length > 0 && (
                    <div className="error-details">
                      <h5>{t('errors')}:</h5>
                      <ul>
                        {bulkOperationResults.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowBulkConfirmation(false)}
                className="btn-secondary"
                disabled={isLoading}
              >
                {t('cancel')}
              </button>
              {!bulkOperationResults && (
                <button
                  type="button"
                  onClick={executeBulkCloseTimeslots}
                  className="btn-danger"
                  disabled={isLoading}
                >
                  {isLoading ? t('closing') : t('confirmAndClose')}
                </button>
              )}
              {bulkOperationResults && (
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkConfirmation(false);
                    setBulkOperationResults(null);
                  }}
                  className="btn-primary"
                >
                  {t('close')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
        onConfirm={confirmation.onConfirm}
        onCancel={confirmation.onCancel}
      />

      <AdminCancelAppointmentDialog
        isOpen={cancelDialogState !== null}
        isLoading={isCancelling}
        onConfirm={handleConfirmCancelAppointment}
        onCancel={() => setCancelDialogState(null)}
      />
    </div>
  );
};

export default TimeslotManagement;